import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Avatar,
  TextField,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  CircularProgress,
  Stack,
  Chip,
  Paper,
  Fade,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import SendIcon from "@mui/icons-material/Send";
import DeleteIcon from "@mui/icons-material/Delete";
import moment from "moment";
import { useAnnouncements, Announcement, AnnouncementComment } from "@/hooks/useAnnouncements";
import { useAuth } from "@/hooks/auth";

interface AnnouncementDrawerProps {
  open: boolean;
  announcement: Announcement | null;
  onClose: () => void;
  isAdmin?: boolean;
}

const getCategoryLabel = (category?: string) => {
  switch (category) {
    case "FEATURE": return "Шинэ Боломж";
    case "UPDATE": return "Шинэчлэлт";
    case "PROMOTION": return "Урамшуулал";
    case "MAINTENANCE": return "Засвар үйлчилгээ";
    case "GENERAL": return "Ерөнхий";
    case "OUTAGE": return "Саатал";
    default: return category || "Ерөнхий";
  }
};

export const AnnouncementDrawer: React.FC<AnnouncementDrawerProps> = ({
  open,
  announcement,
  onClose,
  isAdmin = false
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  const { getComments, addComment, deleteComment, getReactions, toggleReaction } = useAnnouncements();
  
  const [comments, setComments] = useState<AnnouncementComment[]>([]);
  const [reactions, setReactions] = useState<{ [key: string]: number }>({});
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [userReaction, setUserReaction] = useState<string | null>(null);

  useEffect(() => {
    if (open && announcement) {
      fetchCommentsAndReactions();
    }
  }, [open, announcement]);

  const fetchCommentsAndReactions = async () => {
    if (!announcement) return;
    setLoadingComments(true);
    try {
      const [fetchedComments, fetchedReactions] = await Promise.all([
        getComments(announcement.id),
        getReactions(announcement.id)
      ]);
      setComments(fetchedComments);
      setReactions(fetchedReactions);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !announcement) return;
    const success = await addComment(announcement.id, newComment.trim());
    if (success) {
      setNewComment("");
      fetchCommentsAndReactions();
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    const success = await deleteComment(commentId);
    if (success) {
      fetchCommentsAndReactions();
    }
  };

  const handleToggleReaction = async (type: string) => {
    if (!announcement) return;
    const success = await toggleReaction(announcement.id, type);
    if (success) {
      setUserReaction(prev => prev === type ? null : type);
      fetchCommentsAndReactions();
    }
  };

  if (!announcement) return null;

  const categoryLabel = getCategoryLabel(announcement.category);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { 
          width: { xs: "100%", sm: 700 }, 
          p: 0, 
          display: 'flex', 
          flexDirection: 'column',
          backgroundColor: theme.palette.mode === "dark" ? "#0f172a" : "#f8fafc",
        },
      }}
    >
      {/* Dark Premium Header */}
      <Box sx={{ 
        height: 140, 
        background: "linear-gradient(135deg, #0b192c 0%, #1a365d 100%)", 
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        p: 3
      }}>
        <IconButton 
          onClick={onClose} 
          sx={{ 
            position: 'absolute', 
            top: 16, 
            right: 16, 
            color: 'white',
            backgroundColor: 'rgba(0,0,0,0.2)',
            backdropFilter: 'blur(10px)',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.4)' }
          }}
        >
          <CloseIcon />
        </IconButton>
        <Chip 
          label={categoryLabel} 
          size="small" 
          sx={{ 
            alignSelf: 'flex-start',
            backgroundColor: 'rgba(255,255,255,0.2)', 
            color: 'white', 
            fontWeight: 700,
            backdropFilter: 'blur(5px)',
            mb: 1
          }} 
        />
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500, letterSpacing: 0.5 }}>
          {moment(announcement.publishedAt || announcement.createdAt).format("LL, LT")}
        </Typography>
      </Box>

      {/* Main Content Area */}
      <Box sx={{ flexGrow: 1, overflowY: "auto", position: 'relative' }}>
        
        {/* Article Body */}
        <Box sx={{ p: 4, pb: 10, backgroundColor: theme.palette.background.paper, borderBottomLeftRadius: 24, borderBottomRightRadius: 24, boxShadow: "0 4px 20px rgba(0,0,0,0.03)" }}>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 3, lineHeight: 1.2, color: "text.primary" }}>
            {announcement.title}
          </Typography>
          <Typography variant="body1" sx={{ 
            whiteSpace: "pre-wrap", 
            lineHeight: 1.8, 
            color: "text.secondary", 
            fontSize: "1.05rem",
            letterSpacing: 0.2
          }}>
            {announcement.content}
          </Typography>
        </Box>

        {/* Floating Reactions Pill */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: -3, mb: 4, position: 'relative', zIndex: 10 }}>
          <Paper elevation={4} sx={{ 
            display: 'inline-flex', 
            borderRadius: 8, 
            px: 2, 
            py: 0.5, 
            bgcolor: theme.palette.background.paper,
            alignItems: 'center',
            gap: 1
          }}>
             <Button 
                sx={{ borderRadius: 6, px: 2, color: userReaction === 'LIKE' ? "primary.main" : "text.secondary", fontWeight: 600 }}
                startIcon={userReaction === 'LIKE' ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
                onClick={() => handleToggleReaction('LIKE')}
             >
                Таалагдсан ({reactions['LIKE'] || 0})
             </Button>
             <Divider orientation="vertical" flexItem sx={{ my: 1 }} />
             <Button 
                sx={{ borderRadius: 6, px: 2, color: userReaction === 'HEART' ? "#e91e63" : "text.secondary", fontWeight: 600 }}
                startIcon={userReaction === 'HEART' ? <FavoriteIcon sx={{ color: '#e91e63' }} /> : <FavoriteBorderIcon />}
                onClick={() => handleToggleReaction('HEART')}
             >
                Хайр ({reactions['HEART'] || 0})
             </Button>
          </Paper>
        </Box>

        {/* Comments Section */}
        <Box sx={{ px: 3, pb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 3, color: "text.primary" }}>
            Сэтгэгдэл ({comments.length})
          </Typography>

          {loadingComments ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={30} />
            </Box>
          ) : comments.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4, fontStyle: 'italic' }}>
              Одоогоор сэтгэгдэл алга байна. Анхны сэтгэгдлийг үлдээгээрэй!
            </Typography>
          ) : (
            <List sx={{ p: 0 }}>
              {comments.map((comment) => (
                <Fade in key={comment.id}>
                  <ListItem alignItems="flex-start" sx={{ px: 0, py: 1.5 }}>
                    <ListItemAvatar sx={{ minWidth: 48 }}>
                      <Avatar src={comment.user?.avatar} sx={{ width: 36, height: 36, bgcolor: theme.palette.primary.main, fontWeight: 700 }}>
                        {comment.user?.firstName?.[0] || "U"}
                      </Avatar>
                    </ListItemAvatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Paper elevation={0} sx={{ 
                        p: 2, 
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#ffffff', 
                        borderRadius: 3,
                        borderTopLeftRadius: 4,
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.02)"
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
                            {comment.user?.firstName} {comment.user?.lastName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.disabled", fontWeight: 500 }}>
                            {moment(comment.createdAt).fromNow()}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.5 }}>
                          {comment.content}
                        </Typography>
                      </Paper>
                      {(isAdmin || user?.id === comment.user?.id) && (
                        <Box sx={{ display: 'flex', mt: 0.5, ml: 1 }}>
                           <Button 
                             size="small" 
                             color="error" 
                             startIcon={<DeleteIcon fontSize="small" />}
                             sx={{ minWidth: 0, p: '2px 8px', fontSize: '0.75rem', textTransform: 'none', fontWeight: 600, color: 'text.disabled', '&:hover': { color: 'error.main', bgcolor: 'transparent' } }}
                             onClick={() => handleDeleteComment(comment.id)}
                           >
                             Устгах
                           </Button>
                        </Box>
                      )}
                    </Box>
                  </ListItem>
                </Fade>
              ))}
            </List>
          )}
        </Box>
      </Box>

      {/* Sticky Comment Input */}
      <Box sx={{ 
        p: 2, 
        bgcolor: theme.palette.background.paper, 
        borderTop: `1px solid ${theme.palette.divider}`,
        boxShadow: "0 -4px 20px rgba(0,0,0,0.03)"
      }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-end">
          <Avatar src={user?.avatar} sx={{ width: 40, height: 40, mb: 0.5 }}>
             {user?.firstName?.[0] || "U"}
          </Avatar>
          <TextField
            fullWidth
            placeholder="Сэтгэгдэл үлдээх..."
            variant="outlined"
            size="small"
            multiline
            maxRows={4}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleAddComment();
              }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 4,
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
                '& fieldset': { border: 'none' },
                '&:hover fieldset': { border: 'none' },
                '&.Mui-focused fieldset': { border: `1px solid ${theme.palette.primary.main}` },
              }
            }}
          />
          <IconButton 
            color="primary" 
            onClick={handleAddComment} 
            disabled={!newComment.trim() || loadingComments}
            sx={{ 
              bgcolor: newComment.trim() ? 'primary.main' : 'action.disabledBackground', 
              color: 'white', 
              mb: 0.5,
              width: 40,
              height: 40,
              '&:hover': { bgcolor: 'primary.dark' },
              '&.Mui-disabled': { color: 'white', opacity: 0.5 }
            }}
          >
            <SendIcon fontSize="small" sx={{ ml: 0.5 }} />
          </IconButton>
        </Stack>
      </Box>
    </Drawer>
  );
};
